import React, { useState, useEffect } from 'react';
import { ShoppingCart, FileText, Trash2, RefreshCw, Download, ChevronDown, ChevronUp } from 'lucide-react';

const OrdersManager = () => {
  const [orders, setOrders] = useState([]);
  const [forms, setForms] = useState([]);
  const [activeSection, setActiveSection] = useState('orders');
  const [expandedId, setExpandedId] = useState(null);

  const loadData = () => {
    try {
      const savedOrders = JSON.parse(localStorage.getItem('checkoutOrders') || '[]');
      const savedForms  = JSON.parse(localStorage.getItem('certificateFormSubmissions') || '[]');
      setOrders(savedOrders);
      setForms(savedForms);
    } catch (e) {
      console.warn('Error loading data:', e);
    }
  };

  useEffect(() => { loadData(); }, []);

  const deleteOrder = (index) => {
    if (!window.confirm('Bu siparişi silmek istediğinizden emin misiniz?')) return;
    const updated = orders.filter((_, i) => i !== index);
    localStorage.setItem('checkoutOrders', JSON.stringify(updated));
    setOrders(updated);
  };

  const deleteForm = (index) => {
    if (!window.confirm('Bu formu silmek istediğinizden emin misiniz?')) return;
    const updated = forms.filter((_, i) => i !== index);
    localStorage.setItem('certificateFormSubmissions', JSON.stringify(updated));
    setForms(updated);
  };

  const updateOrderStatus = (index, status) => {
    const updated = [...orders];
    updated[index] = { ...updated[index], status };
    localStorage.setItem('checkoutOrders', JSON.stringify(updated));
    setOrders(updated);
  };

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const keys = Object.keys(data[0]).filter(k => typeof data[0][k] !== 'object');
    const rows = [keys.join(',')];
    data.forEach(row => {
      rows.push(keys.map(k => `"${String(row[k] ?? '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('de-DE');
  };

  const statusBadge = (status) => {
    const map = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Bekliyor' },
      paid:    { color: 'bg-green-100 text-green-800',  label: 'Ödendi' },
      new:     { color: 'bg-blue-100 text-blue-800',    label: 'Yeni' },
      done:    { color: 'bg-purple-100 text-purple-800', label: 'Tamamlandı' },
      cancelled: { color: 'bg-red-100 text-red-800',    label: 'İptal' },
    };
    const s = map[status] || { color: 'bg-neutral-100 text-neutral-600', label: status || 'Bilinmiyor' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.color}`}>{s.label}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-neutral-800">Siparişler & Formlar</h2>
          <p className="text-neutral-500 text-sm mt-1">
            {orders.length} sipariş · {forms.length} sertifika başvurusu
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-lg text-sm font-medium transition-colors"
          >
            <RefreshCw className="h-4 w-4" /> Yenile
          </button>
          {activeSection === 'orders' && orders.length > 0 && (
            <button
              onClick={() => exportCSV(
                orders.map(o => ({
                  orderId: o.orderId,
                  tarih: o.confirmedAt,
                  agac: o.trees,
                  tutar: o.price,
                  odeme: o.paymentMethod,
                  ad: o.customerInfo?.firstName,
                  soyad: o.customerInfo?.lastName,
                  email: o.customerInfo?.email,
                  telefon: o.customerInfo?.phone,
                  durum: o.status
                })),
                'siparisler.csv'
              )}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="h-4 w-4" /> CSV İndir
            </button>
          )}
          {activeSection === 'forms' && forms.length > 0 && (
            <button
              onClick={() => exportCSV(
                forms.map(f => ({
                  id: f.id,
                  tarih: f.submittedAt,
                  tip: f.type,
                  ad: f.name,
                  firma: f.company,
                  website: f.website,
                  sektor: f.sector,
                  email: f.email,
                  telefon: f.phone,
                  mesaj: f.message,
                  durum: f.status
                })),
                'basvurular.csv'
              )}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="h-4 w-4" /> CSV İndir
            </button>
          )}
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 bg-neutral-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveSection('orders')}
          className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
            activeSection === 'orders'
              ? 'bg-white shadow text-neutral-800'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <ShoppingCart className="h-4 w-4" />
          Siparişler
          {orders.length > 0 && (
            <span className="bg-green-500 text-white text-xs rounded-full px-1.5 py-0.5">{orders.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveSection('forms')}
          className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
            activeSection === 'forms'
              ? 'bg-white shadow text-neutral-800'
              : 'text-neutral-500 hover:text-neutral-700'
          }`}
        >
          <FileText className="h-4 w-4" />
          Sertifika Başvuruları
          {forms.length > 0 && (
            <span className="bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5">{forms.length}</span>
          )}
        </button>
      </div>

      {/* ── ORDERS ─────────────────────────────────────────────────────────── */}
      {activeSection === 'orders' && (
        <div className="space-y-3">
          {orders.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-neutral-200">
              <ShoppingCart className="h-12 w-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500">Henüz sipariş bulunmuyor.</p>
            </div>
          ) : (
            orders.map((order, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-neutral-200 overflow-hidden shadow-sm">
                {/* Summary Row */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-neutral-50 transition-colors"
                  onClick={() => setExpandedId(expandedId === `o-${idx}` ? null : `o-${idx}`)}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex-shrink-0">
                      {statusBadge(order.status)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-neutral-800 truncate">{order.orderId}</p>
                      <p className="text-sm text-neutral-500">{formatDate(order.confirmedAt)}</p>
                    </div>
                    <div className="hidden sm:block text-sm text-neutral-600">
                      {order.trees} ağaç · <strong>€{Number(order.price).toFixed(2)}</strong>
                    </div>
                    <div className="hidden md:block">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${order.paymentMethod === 'paypal' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                        {order.paymentMethod === 'paypal' ? 'PayPal' : 'Banka Havalesi'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteOrder(idx); }}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Sil"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    {expandedId === `o-${idx}` ? <ChevronUp className="h-4 w-4 text-neutral-400" /> : <ChevronDown className="h-4 w-4 text-neutral-400" />}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedId === `o-${idx}` && (
                  <div className="border-t border-neutral-100 p-5 bg-neutral-50 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Sipariş Bilgileri</h4>
                      <dl className="space-y-1 text-sm">
                        <div className="flex gap-2"><dt className="text-neutral-500 w-28">Sipariş No:</dt><dd className="font-mono text-neutral-800 font-medium">{order.orderId}</dd></div>
                        <div className="flex gap-2"><dt className="text-neutral-500 w-28">Ağaç sayısı:</dt><dd className="text-neutral-800">{order.trees}</dd></div>
                        <div className="flex gap-2"><dt className="text-neutral-500 w-28">Tutar:</dt><dd className="font-bold text-green-700">€{Number(order.price).toFixed(2)}</dd></div>
                        <div className="flex gap-2"><dt className="text-neutral-500 w-28">Ödeme:</dt><dd className="text-neutral-800">{order.paymentMethod === 'paypal' ? 'PayPal' : 'Banka Havalesi'}</dd></div>
                        {order.paypalOrderId && <div className="flex gap-2"><dt className="text-neutral-500 w-28">PayPal ID:</dt><dd className="font-mono text-xs text-neutral-600">{order.paypalOrderId}</dd></div>}
                        <div className="flex gap-2"><dt className="text-neutral-500 w-28">Tarih:</dt><dd className="text-neutral-800">{formatDate(order.confirmedAt)}</dd></div>
                      </dl>
                    </div>
                    {order.customerInfo && (
                      <div>
                        <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Müşteri Bilgileri</h4>
                        <dl className="space-y-1 text-sm">
                          <div className="flex gap-2"><dt className="text-neutral-500 w-20">Ad:</dt><dd className="text-neutral-800">{order.customerInfo.firstName} {order.customerInfo.lastName}</dd></div>
                          <div className="flex gap-2"><dt className="text-neutral-500 w-20">E-posta:</dt><dd className="text-neutral-800 break-all">{order.customerInfo.email || '-'}</dd></div>
                          <div className="flex gap-2"><dt className="text-neutral-500 w-20">Telefon:</dt><dd className="text-neutral-800">{order.customerInfo.phone || '-'}</dd></div>
                        </dl>
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Durum Güncelle</h4>
                      <div className="flex flex-wrap gap-2">
                        {['pending', 'paid', 'done', 'cancelled'].map(s => (
                          <button
                            key={s}
                            onClick={() => updateOrderStatus(idx, s)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                              order.status === s
                                ? 'bg-primary-500 text-white border-primary-500'
                                : 'bg-white text-neutral-600 border-neutral-200 hover:border-primary-300'
                            }`}
                          >
                            {{ pending: 'Bekliyor', paid: 'Ödendi', done: 'Tamamlandı', cancelled: 'İptal' }[s]}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* ── CERTIFICATE FORMS ───────────────────────────────────────────────── */}
      {activeSection === 'forms' && (
        <div className="space-y-3">
          {forms.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-neutral-200">
              <FileText className="h-12 w-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500">Henüz başvuru bulunmuyor.</p>
            </div>
          ) : (
            forms.map((form, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-neutral-200 overflow-hidden shadow-sm">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-neutral-50 transition-colors"
                  onClick={() => setExpandedId(expandedId === `f-${idx}` ? null : `f-${idx}`)}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="flex-shrink-0">
                      {statusBadge(form.status || 'new')}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-neutral-800 truncate">
                        {form.type === 'corporate' ? `${form.company} – ` : ''}{form.name}
                      </p>
                      <p className="text-sm text-neutral-500 truncate">{form.email} · {form.website}</p>
                    </div>
                    <div className="hidden md:block text-xs text-neutral-400">
                      {formatDate(form.submittedAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteForm(idx); }}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Sil"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    {expandedId === `f-${idx}` ? <ChevronUp className="h-4 w-4 text-neutral-400" /> : <ChevronDown className="h-4 w-4 text-neutral-400" />}
                  </div>
                </div>

                {expandedId === `f-${idx}` && (
                  <div className="border-t border-neutral-100 p-5 bg-neutral-50 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Başvuru Detayı</h4>
                      <dl className="space-y-1 text-sm">
                        <div className="flex gap-2"><dt className="text-neutral-500 w-24">Tip:</dt><dd className="text-neutral-800">{form.type === 'corporate' ? 'Kurumsal' : 'Bireysel'}</dd></div>
                        <div className="flex gap-2"><dt className="text-neutral-500 w-24">Ad Soyad:</dt><dd className="text-neutral-800">{form.name}</dd></div>
                        {form.company && <div className="flex gap-2"><dt className="text-neutral-500 w-24">Firma:</dt><dd className="text-neutral-800">{form.company}</dd></div>}
                        {form.sector && <div className="flex gap-2"><dt className="text-neutral-500 w-24">Sektör:</dt><dd className="text-neutral-800">{form.sector}</dd></div>}
                        <div className="flex gap-2"><dt className="text-neutral-500 w-24">Website:</dt><dd className="text-neutral-800 break-all">{form.website}</dd></div>
                        <div className="flex gap-2"><dt className="text-neutral-500 w-24">E-posta:</dt><dd className="text-neutral-800 break-all">{form.email}</dd></div>
                        {form.phone && <div className="flex gap-2"><dt className="text-neutral-500 w-24">Telefon:</dt><dd className="text-neutral-800">{form.phone}</dd></div>}
                        <div className="flex gap-2"><dt className="text-neutral-500 w-24">Tarih:</dt><dd className="text-neutral-800">{formatDate(form.submittedAt)}</dd></div>
                      </dl>
                    </div>
                    {form.message && (
                      <div>
                        <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Mesaj</h4>
                        <p className="text-sm text-neutral-700 bg-white rounded-lg p-3 border border-neutral-200">{form.message}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default OrdersManager;
